import { defaultCountries, usePhoneInput } from "react-international-phone";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { useTranslation } from "./i18n";

/**
 * @typedef {"text" | "number" | "password" | "select" | "phone" | "percent"} InputType
 */

/**
 * @typedef {{value: string, name: string}} SelectOption
 */

/**
 * @param {object} props
 * @param {string} props.id
 * @param {string} [props.label]
 * @param {string} [props.placeholder]
 * @param {InputType} props.type
 * @param {React.ChangeEventHandler<HTMLInputElement | HTMLSelectElement>} props.onChange
 * @param {string | number} [props.value]
 * @param {string} [props.name]
 * @param {boolean} [props.required]
 * @param {boolean} [props.shoRequired]
 * @param {boolean} [props.disabled]
 * @param {boolean} [props.checked]
 * @param {boolean} [props.autoFocus]
 * @param {Array<SelectOption>} [props.options]
 * @param {React.WheelEventHandler<HTMLInputElement>} [props.onWheel]
 * @returns {React.Component}
 */
function CyberpassInput({
  id,
  label,
  placeholder,
  type,
  onChange,
  value,
  name,
  required,
  showRequired,
  disabled,
  checked,
  autoFocus,
  options,
  onWheel,
}) {
  const { t } = useTranslation();
  const required_obj = { required: Boolean(required) };
  disabled = disabled == null ? false : disabled;
  checked = checked == null ? false : checked;
  showRequired = showRequired == null ? false : showRequired;
  let innerOnChange = (
    /** @type {import("react").ChangeEvent<HTMLInputElement | HTMLSelectElement>} */ evt,
  ) => onChange && onChange(evt);

  if (type === "phone") {
    return (
      <CyberpassPhoneInput
        required={required}
        id={id}
        label={label}
        value={value}
        onChange={innerOnChange}
      />
    );
  }

  if (type === "percent") {
    type = "text";
    innerOnChange = (evt) => {
      let percent = parseInt(evt.target.value.replace("%", ""));
      if (Number.isNaN(percent)) {
        percent = 0;
      }
      if (percent > 100) {
        percent = 100;
      } else if (percent < 0) {
        percent = 0;
      }
      evt.target.value = `${percent}%`;
      onChange(evt);
    };
  }

  return (
    <div>
      <div className="flex flex-row space-x-2 items-center mb-3 ">
        {label && (
          <label className="block text-sm font-medium text-gray-900 dark:text-white">
            {label}{" "}
            {required && showRequired && (
              <span className="text-red-200">*</span>
            )}
          </label>
        )}
        {disabled && <p className="text-c-gray-400">(нельзя редактировать)</p>}
      </div>
      {type !== "select" ? (
        <input
          autoFocus={autoFocus}
          disabled={disabled}
          type={type}
          id={id}
          onChange={innerOnChange}
          {...(type === "checkbox" ? { checked: checked } : { value: value })}
          name={name}
          className={`bg-c-gray-500 text-c-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-c-gray-500 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500`}
          placeholder={placeholder}
          onWheel={onWheel}
          {...required_obj}
        />
      ) : (
        <select
          onChange={innerOnChange}
          value={value}
          disabled={disabled}
          className={`bg-c-gray-500 text-c-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-c-gray-500 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500`}
        >
          {required && (
            <option value="" disabled>
              {t("common.selectPlaceholder", {
                label: label?.toLowerCase() || t("common.value"),
              })}
            </option>
          )}
          {options &&
            options.map((opt) => (
              <option
                key={opt.value}
                value={opt.value}
                className="border-none ring-0"
              >
                {opt.name}
              </option>
            ))}
        </select>
      )}
    </div>
  );
}

/**
 * @param {object} props
 * @param {string} props.id
 * @param {string} [props.label]
 * @param {boolean} [required]
 * @param {string | number} [props.value]
 * @param {React.ChangeEventHandler<HTMLInputElement | HTMLSelectElement>} props.onChange
 * @returns {React.Component}
 */
function CyberpassPhoneInput({ id, label, required, value, onChange }) {
  const requiredObj = { required: Boolean(required) };
  const innerOnChange = (/** @type {{ phone: any; }} */ evt) => {
    console.log(`Hello from inner on change with`, evt);
    evt["target"] = { value: evt.phone };
    onChange(evt);
  };
  const { inputValue, handlePhoneValueChange, inputRef } = usePhoneInput({
    defaultCountry: "ru",
    value,
    countries: defaultCountries,
    onChange: innerOnChange,
  });

  return (
    <>
      <label className="block mb-3 text-sm font-medium text-gray-900 dark:text-white">
        {label} {required && <span className="text-red-200">*</span>}
      </label>
      <input
        type="tel"
        id={id}
        value={inputValue}
        ref={inputRef}
        onChange={handlePhoneValueChange}
        className="bg-gray-50 text-gray-900 text-sm rounded-lg focus:ring-blue-500 block w-full p-2.5 dark:bg-c-gray-500 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500"
        {...requiredObj}
      />
    </>
  );
}

/**
 * @param {object} props
 * @param {any} [props.children]
 * @param {"button" | "submit" | "reset"} [props.type]
 * @param {"primary" | "secondary" | "secondary-light" | "green" | "danger" | "primary-white"} [props.style]
 * @param {"auto" | "full" | "fit"} [props.width]
 * @param {() => void} [props.onClick]
 * @param {React.ElementType} [props.Icon]
 * @param {"regular" | "l" | "small"} [props.iconSize]
 * @param string [className]
 * @param boolean [props.disabled]
 * @returns {React.Component}
 */
function CyberpassButton({
  children,
  type,
  style,
  width,
  onClick,
  Icon,
  iconSize,
  className,
  disabled,
}) {
  let colors =
    "bg-purple-700 hover:bg-purple-800 dark:bg-accent dark:hover:bg-accent-hover";

  if (width == null) {
    width = "auto";
  }

  if (disabled) {
    colors = "bg-c-gray-600 text-c-gray-400 cursor-not-allowed";
  } else {
    if (style === "secondary") {
      colors = "dark:bg-c-gray-600 dark:hover:bg-accent-hover";
    }

    if (style === "secondary-light") {
      colors = "dark:bg-c-gray-500 dark:hover:bg-accent-hover";
    }

    if (style === "green") {
      colors = "dark:bg-c-green-500 dark:hover:bg-c-green-600";
    }

    if (style === "danger") {
      colors = "dark:bg-c-red-500 dark:hover:bg-c-red-600";
      if (Icon == null) {
        Icon = XMarkIcon;
        iconSize = "small";
      }
    }

    if (style === "primary-white") {
      colors = "bg-white hover:bg-accent-hover text-accent";
    }
  }

  let iconStyle = "h-3 mr-1";
  if (iconSize === "l") {
    iconStyle = "h-14";
  } else if (iconSize === "small") {
    iconStyle = "h-2 mr-2";
  } else if (iconSize) {
    iconStyle = iconSize;
  }

  return (
    <button
      type={type ? type : "button"}
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      className={`whitespace-nowrap w-${width} focus:outline-none font-medium rounded-lg px-3 py-1 text-center ${colors} ${className ? className : ""}`}
    >
      {Icon ? (
        <div className="flex flex-row justify-center items-center space-x-4">
          <Icon className={iconStyle} />
          {children}
        </div>
      ) : (
        children
      )}
    </button>
  );
}

export { CyberpassInput, CyberpassPhoneInput, CyberpassButton };
